import Vector2 from "./Vector2";
import Rect from "./Rect";

export function valueInRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}

function onSegment(p: Vector2, q: Vector2, r: Vector2) {
  if (
    q.x <= Math.max(p.x, r.x) &&
    q.x >= Math.min(p.x, r.x) &&
    q.y <= Math.max(p.y, r.y) &&
    q.y >= Math.min(p.y, r.y)
  )
    return true;
  return false;
}

// To find orientation of ordered triplet (p, q, r).
// The function returns following values
// 0 --> p, q and r are colinear
// 1 --> Clockwise
// 2 --> Counterclockwise
function orientation(p: Vector2, q: Vector2, r: Vector2) {
  var val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);

  if (val == 0) return 0; // colinear
  return val > 0 ? 1 : 2; // clock or counterclock wise
}

function doIntersect(
  p1: Vector2,
  q1: Vector2,
  p2: Vector2,
  q2: Vector2
): boolean {
  // Find the four orientations needed for general and
  // special cases
  var o1 = orientation(p1, q1, p2);
  var o2 = orientation(p1, q1, q2);
  var o3 = orientation(p2, q2, p1);
  var o4 = orientation(p2, q2, q1);

  // General case
  if (o1 != o2 && o3 != o4) return true;

  // Special Cases
  // p1, q1 and p2 are colinear and p2 lies on segment p1q1
  if (o1 == 0 && onSegment(p1, p2, q1)) return true;

  // p1, q1 and p2 are colinear and q2 lies on segment p1q1
  if (o2 == 0 && onSegment(p1, q2, q1)) return true;

  // p2, q2 and p1 are colinear and p1 lies on segment p2q2
  if (o3 == 0 && onSegment(p2, p1, q2)) return true;

  // p2, q2 and q1 are colinear and q1 lies on segment p2q2
  if (o4 == 0 && onSegment(p2, q1, q2)) return true;

  return false; // Doesn't fall in any of the above cases
}

export function pointInPolygon(polygon: Array<Vector2>, p: Vector2): boolean {
  // There must be at least 3 vertices in polygon[]
  if (polygon.length < 3) return false;

  // Create a point for line segment from p to infinite
  var extreme = new Vector2(1000000000000, p.y);

  // Count intersections of the above line with sides of polygon
  var count = 0,
    i = 0;
  do {
    var next = (i + 1) % polygon.length;

    // Check if the line segment from 'p' to 'extreme' intersects
    // with the line segment from 'polygon[i]' to 'polygon[next]'
    if (doIntersect(polygon[i], polygon[next], p, extreme)) {
      // If the point 'p' is colinear with line segment 'i-next',
      // then check if it lies on segment. If it lies, return true,
      // otherwise false
      if (orientation(polygon[i], p, polygon[next]) == 0)
        return onSegment(polygon[i], p, polygon[next]);

      count++;
    }
    i = next;
  } while (i != 0);

  // Return true if count is odd, false otherwise
  return count % 2 == 1;
}

export function pointInEllipse(
  center: Vector2,
  radius: Vector2,
  rotation: number,
  p: Vector2
): boolean {
  var cosR = Math.cos((rotation * Math.PI) / 180);
  var sinR = Math.sin((rotation * Math.PI) / 180);

  var factor =
    Math.pow(cosR * (p.x - center.x) + sinR * (p.y - center.y), 2) /
      Math.pow(radius.x, 2) +
    Math.pow(sinR * (p.x - center.x) - cosR * (p.y - center.y), 2) /
      Math.pow(radius.y, 2);

  return factor < 1;
}

export function pointToLineDistance(p: Vector2, p1: Vector2, p2: Vector2) {
  var A = p.x - p1.x;
  var B = p.y - p1.y;
  var C = p2.x - p1.x;
  var D = p2.y - p1.y;

  var dot = A * C + B * D;
  var len_sq = C * C + D * D;
  var param = -1;
  if (len_sq != 0)
    //in case of 0 length line
    param = dot / len_sq;

  var xx, yy;

  if (param < 0) {
    xx = p1.x;
    yy = p1.y;
  } else if (param > 1) {
    xx = p2.x;
    yy = p2.y;
  } else {
    xx = p1.x + param * C;
    yy = p1.y + param * D;
  }

  var dx = p.x - xx;
  var dy = p.y - yy;
  return Math.sqrt(dx * dx + dy * dy);
}

export function pointOnRect(
  x: number,
  y: number,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number
): Vector2 {
  //assert minX <= maxX;
  //assert minY <= maxY;
  if (minX < x && x < maxX && minY < y && y < maxY) {
    return null;
  }

  var midX = (minX + maxX) / 2;
  var midY = (minY + maxY) / 2;
  // if (midX - x == 0) -> m == ±Inf -> minYx/maxYx == x (because value / ±Inf = ±0)
  var m = (midY - y) / (midX - x);

  if (x <= midX) {
    // check "left" side
    var minXy = m * (minX - x) + y;
    if (minY <= minXy && minXy <= maxY) return new Vector2(minX, minXy);
  }

  if (x >= midX) {
    // check "right" side
    var maxXy = m * (maxX - x) + y;
    if (minY <= maxXy && maxXy <= maxY) return new Vector2(maxX, maxXy);
  }

  if (y <= midY) {
    // check "top" side
    var minYx = (minY - y) / m + x;
    if (minX <= minYx && minYx <= maxX) return new Vector2(minYx, minY);
  }

  if (y >= midY) {
    // check "bottom" side
    var maxYx = (maxY - y) / m + x;
    if (minX <= maxYx && maxYx <= maxX) return new Vector2(maxYx, maxY);
  }

  // edge case when finding midpoint intersection: m = 0/0 = NaN
  if (x === midX && y === midY) return new Vector2(x, y);

  return null;
}
